import React, { useState, useEffect } from 'react';
import { formatPrice } from '../utils/currency';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { restaurantApi, customerApi } from '../api/apiClient';
import PhoneOtpForm, { buildOtpLabels } from '../components/PhoneOtpForm';
import { useEmailAuthEnabled } from '../hooks/useEmailAuthEnabled';
import { useLanguage } from '../context/LanguageContext';
import { useCoreT } from '../context/CoreLangContext';
import { resolveMenuTheme, menuCssVars } from '../theme/menuTheme';
import { identify, trackViewCart, trackBeginCheckout, trackPurchase } from '../analytics/track';

// ─────────────────────────────────────────────────────────────────────────────
// Modal 1 — Not logged in: show auth form (login / register)
// ─────────────────────────────────────────────────────────────────────────────
function LoginModal({ onClose, onSuccess }) {
  const { registerCustomer, loginCustomer, sendCustomerOtp, verifyCustomerOtp } = useAuth();
  const { tu, restaurantLang } = useLanguage();
  const emailAuth = useEmailAuthEnabled() === true;   // email tab only when the platform allows it
  const [method,  setMethod]  = useState('phone');    // 'phone' | 'email'
  const [mode,    setMode]    = useState('login');
  const [form,    setForm]    = useState({ name: '', email: '', password: '', phone: '', address: '' });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Email and password are required.'); return; }
    if (mode === 'register') {
      if (!form.name.trim())          { setError('Name is required.'); return; }
      if (form.password.length < 6)   { setError('Password must be at least 6 characters.'); return; }
      if (!form.phone.trim())         { setError('Phone number is required to place orders.'); return; }
      if (!form.address.trim())       { setError('Delivery address is required to place orders.'); return; }
    }
    setLoading(true);
    const res = mode === 'login'
      ? await loginCustomer(form.email, form.password)
      : await registerCustomer({ name: form.name.trim(), email: form.email, password: form.password, phone: form.phone.trim(), address: form.address.trim() });
    setLoading(false);
    if (res.success) onSuccess(res.customer);
    else setError(res.error);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-icon">🔐</div>
        <h2 className="modal-title">{tu('auth_signin_title')}</h2>
        <p className="modal-sub">{tu('auth_signin_sub')}</p>

        {emailAuth && (
          <div className="modal-tabs" style={{marginBottom:'.75rem'}}>
            <button className={`modal-tab ${method==='phone' ? 'active':''}`} onClick={() => { setMethod('phone'); setError(''); }}>📱 {tu('auth_phone')}</button>
            <button className={`modal-tab ${method==='email' ? 'active':''}`} onClick={() => { setMethod('email'); setError(''); }}>✉️ {tu('auth_email')}</button>
          </div>
        )}

        {(method === 'phone' || !emailAuth) ? (
          <PhoneOtpForm
            askName
            lang={restaurantLang}
            labels={buildOtpLabels(tu)}
            onSend={sendCustomerOtp}
            onVerify={async (phone, code, name) => {
              const res = await verifyCustomerOtp(phone, code, name);
              if (res.success) onSuccess(res.customer);
              return res;
            }}
          />
        ) : (
        <>
        <div className="modal-tabs">
          <button className={`modal-tab ${mode==='login'    ? 'active':''}`} onClick={() => { setMode('login');    setError(''); }}>{tu('auth_signin')}</button>
          <button className={`modal-tab ${mode==='register' ? 'active':''}`} onClick={() => { setMode('register'); setError(''); }}>{tu('auth_register')}</button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-fields">
          {mode === 'register' && (
            <div className="modal-field">
              <label>{tu('auth_name')}</label>
              <input className="modal-input" value={form.name} onChange={set('name')} placeholder="Your name" />
            </div>
          )}
          <div className="modal-field">
            <label>{tu('auth_email')}</label>
            <input className="modal-input" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" />
          </div>
          <div className="modal-field">
            <label>{tu('auth_password')} {mode==='register' && <span style={{color:'rgba(255,255,255,.3)',fontWeight:400}}>(min 6 chars)</span>}</label>
            <input className="modal-input" type="password" value={form.password} onChange={set('password')} placeholder="••••••••" onKeyDown={e => e.key==='Enter' && handleSubmit()} />
          </div>
          {mode === 'register' && (
            <>
              <div className="modal-field">
                <label>{tu('auth_phone')} <span className="req">*required for delivery</span></label>
                <input className="modal-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 234 567 8900" />
              </div>
              <div className="modal-field">
                <label>{tu('auth_address')} <span className="req">*required for delivery</span></label>
                <input className="modal-input" value={form.address} onChange={set('address')} placeholder="123 Main St, City" />
              </div>
            </>
          )}
        </div>

        <button className="modal-submit" onClick={handleSubmit} disabled={loading}>
          {loading ? '…' : mode==='login' ? tu('auth_signin') : tu('auth_create')}
        </button>
        </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal 2 — Logged in but missing phone or address
// ─────────────────────────────────────────────────────────────────────────────
function CompleteProfileModal({ customer, onClose, onSaved }) {
  const { saveCustomerProfile } = useAuth();
  const { tu } = useLanguage();
  const [form,    setForm]    = useState({ phone: customer.phone || '', address: customer.address || '' });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setError('');
    if (!form.phone.trim())   { setError('Phone number is required.'); return; }
    if (!form.address.trim()) { setError('Delivery address is required.'); return; }
    setLoading(true);
    try {
      const res = await customerApi.updateMe({ phone: form.phone.trim(), address: form.address.trim() });
      saveCustomerProfile({ ...customer, ...res.data });
      onSaved({ ...customer, ...res.data });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-icon">📋</div>
        <h2 className="modal-title">{tu('cp_title')}</h2>
        <p className="modal-sub">{tu('cp_sub')}</p>

        {error && <div className="modal-error">{error}</div>}

        <div className="modal-fields">
          <div className="modal-field">
            <label>{tu('auth_phone')}</label>
            <input className="modal-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 234 567 8900" autoFocus />
          </div>
          <div className="modal-field">
            <label>{tu('auth_address')}</label>
            <input className="modal-input" value={form.address} onChange={set('address')} placeholder="123 Main St, City" onKeyDown={e => e.key==='Enter' && handleSave()} />
          </div>
        </div>

        <button className="modal-submit" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving…' : tu('cp_save')}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal 3 — Confirm order with address
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmOrderModal({ customer, total, serviceFee, branchName, onClose, onConfirm, loading, currencyCode }) {
  const { tu } = useLanguage();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-icon">📦</div>
        <h2 className="modal-title">{tu('confirm_title')}</h2>

        <div className="confirm-details">
          {branchName && (
            <div className="confirm-row">
              <span className="confirm-label">🍽️ Branch</span>
              <span className="confirm-value">{branchName}</span>
            </div>
          )}
          <div className="confirm-row">
            <span className="confirm-label">👤 {tu('confirm_name')}</span>
            <span className="confirm-value">{customer.name}</span>
          </div>
          <div className="confirm-row">
            <span className="confirm-label">📞 {tu('confirm_phone')}</span>
            <span className="confirm-value">{customer.phone}</span>
          </div>
          <div className="confirm-row">
            <span className="confirm-label">📍 {tu('confirm_address')}</span>
            <span className="confirm-value">{customer.address}</span>
          </div>
          <div className="confirm-divider" />
          <div className="confirm-row total-row">
            <span className="confirm-label">💰 Total</span>
            <span className="confirm-total">{formatPrice(total + serviceFee, currencyCode)}</span>
          </div>
        </div>

        <p className="confirm-note">{tu('confirm_note')}</p>

        <div className="confirm-actions">
          <button className="confirm-cancel" onClick={onClose}>{tu('confirm_edit')}</button>
          <button className="confirm-go" onClick={onConfirm} disabled={loading}>
            {loading ? 'Placing…' : tu('confirm_yes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Branch Selection Modal — shown when cart has items but no branch selected
// ─────────────────────────────────────────────────────────────────────────────
function BranchSelectModal({ restaurant, onSelect, onClose }) {
  const activeBranches = (restaurant.branches || []).filter(b => b.isActive);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-icon">🍽️</div>
        <h2 className="modal-title">Which branch?</h2>
        <p className="modal-sub" style={{marginBottom:'1.25rem'}}>
          {restaurant.name} has multiple branches. Choose the one you'd like to order from.
        </p>
        <div className="branch-select-list">
          {activeBranches.map(b => (
            <button
              key={b._id || b.slug}
              className="branch-select-btn"
              onClick={() => onSelect(b.slug)}
              style={{ '--primary': restaurant.primaryColor || '#c8a96e' }}
            >
              <div className="branch-select-info">
                <span className="branch-select-name">{b.name}</span>
                {b.address && <span className="branch-select-addr">📍 {b.address}</span>}
                {b.hours   && <span className="branch-select-addr">🕐 {b.hours}</span>}
              </div>
              <span className="branch-select-arrow">→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main CartPage
// ─────────────────────────────────────────────────────────────────────────────
export default function CartPage({ restaurant }) {
  const T = useCoreT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getCartForRestaurant, addItem, removeItem, setQuantity, clearCart, getCartTotal, getCartCount } = useCart();
  const { customer } = useAuth();
  const { tu } = useLanguage();

  // Menu theme palette (shared with the menu + item pages)
  const { theme: menuTheme, colors: menuColors } = resolveMenuTheme(restaurant);

  // Branch from URL — ?branch=slug
  const branchSlug = searchParams.get('branch') || null;
  const activeBranches = (restaurant.branches || []).filter(b => b.isActive);
  const hasBranches = activeBranches.length > 0;
  // Resolved branch object (null = main restaurant / no branches)
  const activeBranch = branchSlug
    ? activeBranches.find(b => b.slug === branchSlug) || null
    : null;
  // State: if restaurant has branches but no branch is selected, let user pick one
  const [selectedBranchSlug, setSelectedBranchSlug] = useState(branchSlug);
  const resolvedBranchSlug = selectedBranchSlug;
  // The main restaurant is orderable only if it actually has products. When it
  // does, a cart built from the main menu must order from MAIN (branchSlug null)
  // — forcing a branch here would validate main product ids against a branch's
  // products and fail ("product not found"). Only force a branch choice when the
  // main menu has no products (branch-only restaurant) and none is selected yet.
  const mainHasProducts = (restaurant.products || []).length > 0;
  const mustPickBranch = hasBranches && !resolvedBranchSlug && !mainHasProducts;

  const [orderPlaced,  setOrderPlaced]  = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState(null);
  const [orderError,   setOrderError]   = useState('');
  const [placing,      setPlacing]      = useState(false);

  // Which modal is open: null | 'branch' | 'login' | 'profile' | 'confirm'
  const [modal, setModal] = useState(null);
  // Customer state that may be updated mid-flow (after profile completion)
  const [liveCustomer, setLiveCustomer] = useState(customer);

  const cartItems  = Object.values(getCartForRestaurant(restaurant.slug));
  const total      = getCartTotal(restaurant.slug);
  const count      = getCartCount(restaurant.slug);
  const serviceFee = total * 0.05;

  // ── Behavioural tracking ──────────────────────────────────────────────────
  useEffect(() => { identify((liveCustomer || customer)?._id || null); }, [liveCustomer, customer]);
  useEffect(() => { if (count > 0) trackViewCart(total + serviceFee, count); /* eslint-disable-next-line */ }, []);

  // ── Step 1: user clicks "Place Order" ──────────────────────────────────────
  const handlePlaceOrderClick = () => {
    trackBeginCheckout(total + serviceFee, count);
    setOrderError('');

    // Branch-only restaurant (main has no products) with no branch chosen yet
    if (mustPickBranch) {
      setModal('branch');
      return;
    }

    // Not logged in → show login modal
    if (!liveCustomer) {
      setModal('login');
      return;
    }

    // Logged in but missing phone or address → profile completion
    if (!liveCustomer.phone?.trim() || !liveCustomer.address?.trim()) {
      setModal('profile');
      return;
    }

    // All good → confirmation modal
    setModal('confirm');
  };

  // ── Branch selection done ────────────────────────────────────────────────────
  const handleBranchSelected = (slug) => {
    setSelectedBranchSlug(slug);
    setModal(null);
    // Now continue the order flow
    if (!liveCustomer) {
      setModal('login');
    } else if (!liveCustomer.phone?.trim() || !liveCustomer.address?.trim()) {
      setModal('profile');
    } else {
      setModal('confirm');
    }
  };

  // ── Called after successful login ───────────────────────────────────────────
  const handleLoginSuccess = (updatedCustomer) => {
    const c = updatedCustomer || customer;
    setLiveCustomer(c);
    setModal(null);
    // Re-check: do they still need to complete profile?
    if (!c?.phone?.trim() || !c?.address?.trim()) {
      setModal('profile');
    } else {
      setModal('confirm');
    }
  };

  // ── Called after profile saved ──────────────────────────────────────────────
  const handleProfileSaved = (updatedCustomer) => {
    setLiveCustomer(updatedCustomer);
    setModal('confirm');
  };

  // ── Actually place the order ────────────────────────────────────────────────
  const handleConfirmOrder = async () => {
    setPlacing(true);
    setOrderError('');
    try {
      const payload = {
        items: cartItems.map(item => ({
          productId: item._id || item.id,
          quantity:  item.quantity,
        })),
        customerName:  liveCustomer.name,
        customerEmail: liveCustomer.email,
        customerPhone: liveCustomer.phone,
        customerAddress: liveCustomer.address,
        branchSlug: resolvedBranchSlug || null,
      };
      const resp = await restaurantApi.placeOrder(restaurant.slug, payload);
      const newOrderId = resp?.data?._id || null;
      setPlacedOrderId(newOrderId);
      if (newOrderId) {
        try {
          const k = `nooshin_orders_${restaurant.slug}`;
          const prev = JSON.parse(localStorage.getItem(k) || '[]');
          localStorage.setItem(k, JSON.stringify([newOrderId, ...prev].slice(0, 10)));
        } catch {}
      }
      trackPurchase({
        _id: resp?.data?._id, orderNumber: resp?.data?.orderNumber,
        total: total + serviceFee, items: cartItems,
      });
      clearCart(restaurant.slug);
      setModal(null);
      setOrderPlaced(true);
    } catch (err) {
      setOrderError(err.message);
      setModal(null);
    } finally {
      setPlacing(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (orderPlaced) {
    return (
      <div className={`cart-page theme-${menuTheme}`} style={menuCssVars(menuColors)}>
        <div className="order-success">
          <div className="success-icon">✅</div>
          <h2 className="success-title">{tu('success_title')}</h2>
          <p className="success-msg">{tu('success_msg')} <strong>{restaurant.name}</strong>. They'll prepare it shortly.</p>
          {liveCustomer && (
            <div className="success-delivery">
              <div>📍 {tu('success_delivering')}: <strong>{liveCustomer.address}</strong></div>
              <div>📞 {tu('success_contact')}: <strong>{liveCustomer.phone}</strong></div>
            </div>
          )}
          <div className="success-actions">
            {placedOrderId && (
              <button className="success-btn primary" onClick={() => navigate(`/order/${placedOrderId}`)}
                style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}>
                📡 {tu('track_cta')}
              </button>
            )}
            <button className="success-btn" onClick={() => navigate('/menu')}>{tu('success_continue')}</button>
            <button className="success-btn" onClick={() => { window.location.href = window.location.origin + '/'; }}>{tu('success_home')}</button>
          </div>
        </div>
        <style>{css(menuColors)}</style>
      </div>
    );
  }

  return (
    <div className={`cart-page theme-${menuTheme}`} style={menuCssVars(menuColors)}>
      <div className="cart-header">
        <button className="cart-back" onClick={() => navigate('/menu')}>{tu('cart_back')}</button>
        <h1 className="cart-title">{tu('cart_title')}</h1>
        {count > 0 && <button className="clear-btn" onClick={() => clearCart(restaurant.slug)}>Clear all</button>}
      </div>

      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <div style={{ fontSize: '4rem' }}>🛒</div>
          <h3>{tu('cart_empty')}</h3>
          <p>Add some delicious items from the menu!</p>
          <button className="go-menu-btn" onClick={() => navigate('/menu')} style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}>{tu('cart_browse')}</button>
        </div>
      ) : (
        <div className="cart-layout">
          <div className="cart-items">
            <div className="cart-restaurant-name">{restaurant.name}</div>
            {cartItems.map(item => (
              <CartItem key={item._id || item.id} item={item}
                restaurantSlug={restaurant.slug} primaryColor={restaurant.primaryColor}
                currencyCode={restaurant.currencyCode} />
            ))}
          </div>

          <div className="cart-summary">
            <h3 className="summary-title">{tu('cart_summary')}</h3>
            <div className="summary-lines">
              {cartItems.map(item => (
                <div key={item._id || item.id} className="summary-line">
                  <span>{item.name} × {item.quantity}</span>
                  <span>{formatPrice(item.price * item.quantity, restaurant?.currencyCode)}</span>
                </div>
              ))}
            </div>
            <div className="summary-divider" />
            <div className="summary-line subtotal"><span>{tu('cart_subtotal')}</span><span>{formatPrice(total, restaurant?.currencyCode)}</span></div>
            <div className="summary-line fee"><span>{tu('cart_service_fee')}</span><span>{formatPrice(serviceFee, restaurant?.currencyCode)}</span></div>
            <div className="summary-line total"><span>{tu('cart_total')}</span><span>{formatPrice(total + serviceFee, restaurant?.currencyCode)}</span></div>

            {/* Customer info preview */}
            <div className="customer-info-box">
              {liveCustomer ? (
                <div className="customer-info-filled">
                  <div className="ci-row"><span>👤</span><span>{liveCustomer.name}</span></div>
                  {liveCustomer.phone   && <div className="ci-row"><span>📞</span><span>{liveCustomer.phone}</span></div>}
                  {liveCustomer.address && <div className="ci-row"><span>📍</span><span>{liveCustomer.address}</span></div>}
                  {(!liveCustomer.phone || !liveCustomer.address) && (
                    <div className="ci-warning">⚠️ Complete your phone &amp; address to order</div>
                  )}
                </div>
              ) : (
                <div className="ci-guest">
                  <span>🔐</span>
                  <span>Sign in to place your order</span>
                </div>
              )}
            </div>

            {/* Branch indicator */}
            {hasBranches && (() => {
              const b = resolvedBranchSlug ? (restaurant.branches||[]).find(x=>x.slug===resolvedBranchSlug) : null;
              if (b) return (
                <div className="cart-branch-banner">
                  <span>📍</span>
                  <span>Ordering from: <strong>{b.name}</strong>{b.address ? ` · ${b.address}` : ''}</span>
                </div>
              );
              if (mainHasProducts) return (
                <div className="cart-branch-banner">
                  <span>📍</span>
                  <span>Ordering from: <strong>{restaurant.name}</strong></span>
                </div>
              );
              return (
                <div className="cart-branch-warn">
                  <span>⚠️</span>
                  <span>You'll choose a branch when placing your order.</span>
                </div>
              );
            })()}

            {orderError && <div className="order-error">{orderError}</div>}

            <button
              className="place-order-btn"
              onClick={handlePlaceOrderClick}
              disabled={placing}
              style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
            >
              {placing ? T.placingOrder : `${tu('cart_place_order')} · ${formatPrice(total + serviceFee, restaurant?.currencyCode)}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === 'branch'  && <BranchSelectModal restaurant={restaurant} onSelect={handleBranchSelected} onClose={() => setModal(null)} />}
      {modal === 'login'   && <LoginModal   onClose={() => setModal(null)} onSuccess={handleLoginSuccess} />}
      {modal === 'profile' && <CompleteProfileModal customer={liveCustomer || customer} onClose={() => setModal(null)} onSaved={handleProfileSaved} />}
      {modal === 'confirm' && <ConfirmOrderModal customer={liveCustomer} total={total} serviceFee={serviceFee} currencyCode={restaurant?.currencyCode} onClose={() => setModal(null)} onConfirm={handleConfirmOrder} loading={placing} branchName={activeBranch?.name} />}

      <style>{css(menuColors)}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function CartItem({ item, restaurantSlug, primaryColor, currencyCode }) {
  const { addItem, removeItem, setQuantity } = useCart();
  const navigate = useNavigate();
  const pid = item._id || item.id;

  return (
    <div className="cart-item" style={{ '--primary': primaryColor }}>
      <img src={item.image} alt={item.name} className="cart-item-img"
        onClick={() => navigate(`/item/${pid}`)} onError={e => e.target.style.display='none'} />
      <div className="cart-item-info">
        <div className="cart-item-name" onClick={() => navigate(`/item/${pid}`)}>{item.name}</div>
        <div className="cart-item-price">{formatPrice(item.price, currencyCode)} each</div>
      </div>
      <div className="cart-item-controls">
        <button className="ci-btn" onClick={() => removeItem(restaurantSlug, pid)}>−</button>
        <span className="ci-qty">{item.quantity}</span>
        <button className="ci-btn ci-btn-add" onClick={() => addItem(restaurantSlug, { ...item, id: pid })}>+</button>
      </div>
      <div className="cart-item-total">{formatPrice(item.price * item.quantity, currencyCode)}</div>
      <button className="cart-item-remove" onClick={() => setQuantity(restaurantSlug, { ...item, id: pid }, 0)}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function css(colors) {
  const pageBg = (colors && colors.bg) || '#0f0f0f';
  return `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${pageBg}; }
    .cart-page { min-height: 100vh; background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; padding: 2rem; transition: background .3s, color .3s; }
    .cart-page, .cart-page * { box-sizing: border-box; }
    .cart-page { overflow-x: hidden; }
    .cart-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; }
    .cart-back { background: none; border: none; color: var(--muted); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 1rem; padding: 0.5rem; border-radius: 8px; transition: color 0.2s; }
    .cart-back:hover { color: var(--text); }
    .cart-title { font-family: 'Playfair Display', serif; font-size: 2rem; font-weight: 700; color: var(--text); flex: 1; }
    .clear-btn { background: none; border: 1px solid rgba(239,68,68,0.3); border-radius: 8px; color: #f87171; padding: 0.4rem 1rem; cursor: pointer; font-size: 0.85rem; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
    .clear-btn:hover { background: rgba(239,68,68,0.1); }
    .empty-cart { text-align: center; padding: 6rem 2rem; display: flex; flex-direction: column; align-items: center; gap: 1rem; color: var(--muted); }
    .empty-cart h3 { color: var(--text); font-size: 1.5rem; }
    .go-menu-btn { margin-top: 1rem; padding: 0.9rem 2rem; border: none; border-radius: 12px; color: var(--accent-text); font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 700; cursor: pointer; }
    .cart-layout { display: grid; grid-template-columns: 1fr 380px; gap: 2rem; max-width: 1100px; margin: 0 auto; }
    @media (max-width: 800px) { .cart-layout { grid-template-columns: 1fr; } .cart-page { padding: 1rem; } .cart-summary { position: static; padding: 1.25rem; } }
    @media (max-width: 480px) { .cart-page { padding: .85rem; } .cart-title { font-size: 1.5rem; } .cart-item { padding: .8rem; gap: .6rem .75rem; } .cart-item-img { width: 54px; height: 54px; } .cart-item-info { flex-basis: calc(100% - 66px); } .cart-item-total { margin-inline-start: auto; } .cart-summary { padding: 1.1rem; } }
    .cart-restaurant-name { font-family: 'Playfair Display', serif; font-size: 1.2rem; color: var(--accent); margin-bottom: 1rem; }
    .cart-items { display: flex; flex-direction: column; gap: 0.75rem; }
    .cart-item { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem; background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 1rem; transition: border-color 0.2s; }
    .cart-item:hover { border-color: var(--accent); }
    .cart-item-img { width: 64px; height: 64px; border-radius: 10px; object-fit: cover; cursor: pointer; flex-shrink: 0; }
    .cart-item-info { flex: 1; min-width: 0; }
    .cart-item-name { font-weight: 600; color: var(--text); cursor: pointer; margin-bottom: 0.25rem; font-size: 0.95rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .cart-item-name:hover { color: var(--accent); }
    .cart-item-price { font-size: 0.8rem; color: var(--faint); }
    .cart-item-controls { display: flex; align-items: center; gap: 0.5rem; }
    .ci-btn { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--border); background: var(--surface-2); color: var(--text); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 1rem; transition: background 0.2s; }
    .ci-btn:hover { background: var(--accent-soft); }
    .ci-btn-add { background: var(--accent); border-color: var(--accent); color: var(--accent-text); }
    .ci-btn-add:hover { opacity: 0.85; }
    .ci-qty { font-weight: 700; font-size: 0.95rem; color: var(--text); min-width: 20px; text-align: center; }
    .cart-item-total { font-weight: 700; color: var(--accent); font-size: 1rem; min-width: 60px; text-align: right; }
    .cart-item-remove { background: none; border: none; color: var(--faint); cursor: pointer; font-size: 0.8rem; padding: 0.25rem; border-radius: 4px; transition: color 0.2s; }
    .cart-item-remove:hover { color: #f87171; }
    .cart-summary { background: var(--surface); border: 1px solid var(--border); border-radius: 20px; padding: 2rem; height: fit-content; position: sticky; top: 2rem; }
    .summary-title { font-family: 'Playfair Display', serif; font-size: 1.3rem; color: var(--text); margin-bottom: 1.5rem; }
    .summary-lines { display: flex; flex-direction: column; gap: 0.6rem; margin-bottom: 1rem; }
    .summary-line { display: flex; justify-content: space-between; font-size: 0.85rem; color: var(--muted); }
    .summary-divider { border: none; border-top: 1px solid var(--border); margin: 1rem 0; }
    .subtotal { color: var(--text); font-weight: 500; }
    .fee { color: var(--faint); font-size: 0.8rem; }
    .total { color: var(--text); font-weight: 700; font-size: 1.1rem; margin-top: 0.5rem; }

    /* Customer info box in summary */
    .customer-info-box { margin-top: 1.5rem; background: var(--surface-2); border: 1px solid var(--border); border-radius: 12px; padding: 1rem; }
    .customer-info-filled { display: flex; flex-direction: column; gap: 0.5rem; }
    .ci-row { display: flex; gap: 0.6rem; font-size: 0.82rem; color: var(--text); align-items: flex-start; }
    .ci-row span:first-child { flex-shrink: 0; }
    .ci-warning { margin-top: 0.5rem; font-size: 0.78rem; color: #fbbf24; }
    .ci-guest { display: flex; gap: 0.6rem; font-size: 0.85rem; color: var(--muted); align-items: center; }

    .order-error { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.25); border-radius: 10px; padding: 0.75rem 1rem; font-size: 0.83rem; color: #fca5a5; margin-top: 1rem; }
    /* Branch banners */
    .cart-branch-banner { display:flex; align-items:center; flex-wrap:wrap; gap:.5rem; background:var(--accent-soft); border:1px solid var(--border); border-radius:10px; padding:.75rem 1.1rem; margin-bottom:1rem; font-size:.85rem; color:var(--text); }
    .cart-branch-banner strong { color:var(--text); }
    .cart-branch-change { margin-left:auto; background:none; border:none; color:var(--accent); cursor:pointer; font-family:inherit; font-size:.8rem; font-weight:700; padding:0; text-decoration:underline; }
    .cart-branch-warn { display:flex; align-items:center; gap:.5rem; background:rgba(251,191,36,.07); border:1px solid rgba(251,191,36,.2); border-radius:10px; padding:.75rem 1.1rem; margin-bottom:1rem; font-size:.82rem; color:rgba(251,191,36,.8); }
    /* Branch select modal */
    .branch-select-list { display:flex; flex-direction:column; gap:.5rem; max-height:320px; overflow-y:auto; }
    .branch-select-btn { display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:1rem 1.25rem; background:var(--surface-2); border:1px solid var(--border); border-radius:14px; cursor:pointer; transition:all .2s; text-align:left; font-family:inherit; color:var(--text); width:100%; }
    .branch-select-btn:hover { background:var(--accent-soft); border-color:var(--accent); }
    .branch-select-info { display:flex; flex-direction:column; gap:.2rem; flex:1; min-width:0; }
    .branch-select-name { font-size:.95rem; font-weight:700; color:var(--text); }
    .branch-select-addr { font-size:.75rem; color:var(--faint); }
    .branch-select-arrow { font-size:1.1rem; color:var(--accent); flex-shrink:0; transition:transform .2s; }
    .branch-select-btn:hover .branch-select-arrow { transform:translateX(3px); }
    .place-order-btn { margin-top: 1.5rem; width: 100%; padding: 1.1rem; border: none; border-radius: 14px; color: var(--accent-text); font-family: 'DM Sans', sans-serif; font-size: 1.05rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s, transform 0.2s; }
    .place-order-btn:hover:not(:disabled) { opacity: 0.9; transform: scale(1.01); }
    .place-order-btn:disabled { opacity: 0.6; cursor: not-allowed; }

    /* Success screen */
    .order-success { max-width: 500px; margin: 8rem auto; text-align: center; padding: 3rem; background: var(--surface); border: 1px solid var(--border); border-radius: 24px; }
    .success-icon { font-size: 4rem; margin-bottom: 1.5rem; }
    .success-title { font-family: 'Playfair Display', serif; font-size: 2rem; color: var(--text); margin-bottom: 1rem; }
    .success-msg { color: var(--muted); line-height: 1.7; margin-bottom: 1.5rem; }
    .success-delivery { background: var(--surface-2); border-radius: 12px; padding: 1rem; margin-bottom: 1.5rem; display: flex; flex-direction: column; gap: 0.5rem; font-size: 0.85rem; color: var(--text); text-align: left; }
    .success-actions { display: flex; flex-direction: column; gap: 0.75rem; }
    .success-btn { padding: 0.9rem; border-radius: 12px; border: none; color: var(--accent-text); font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s; }
    .success-btn:not(.primary) { background: var(--surface-2); color: var(--text); }
    .success-btn:hover { opacity: 0.85; }

    /* ── Modal shared styles ── */
    .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(12px); z-index: 300; display: flex; align-items: center; justify-content: center; padding: 1rem; }
    .modal-box { background: var(--surface); border: 1px solid var(--border); border-radius: 24px; padding: 2rem; width: 100%; max-width: 400px; position: relative; font-family: 'DM Sans', sans-serif; max-height: 90vh; overflow-y: auto; }
    .modal-close { position: absolute; top: 1rem; right: 1rem; background: none; border: none; color: var(--faint); cursor: pointer; font-size: 1rem; padding: 0.25rem 0.5rem; border-radius: 4px; transition: color 0.2s; }
    .modal-close:hover { color: var(--text); }
    .modal-icon { font-size: 2.5rem; margin-bottom: 1rem; }
    .modal-title { font-family: 'Playfair Display', serif; font-size: 1.4rem; color: var(--text); margin-bottom: 0.5rem; }
    .modal-sub { font-size: 0.83rem; color: var(--muted); line-height: 1.6; margin-bottom: 1.5rem; }
    .modal-tabs { display: flex; background: var(--surface-2); border-radius: 10px; padding: 0.25rem; margin-bottom: 1.25rem; border: 1px solid var(--border); }
    .modal-tab { flex: 1; padding: 0.6rem; background: none; border: none; color: var(--muted); cursor: pointer; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.9rem; transition: all 0.2s; }
    .modal-tab.active { background: var(--accent); color: var(--accent-text); font-weight: 700; }
    .modal-error { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); border-radius: 8px; padding: 0.65rem 1rem; color: #fca5a5; font-size: 0.82rem; margin-bottom: 1rem; }
    .modal-fields { display: flex; flex-direction: column; gap: 0.9rem; margin-bottom: 1.25rem; }
    .modal-field { display: flex; flex-direction: column; gap: 0.4rem; }
    .modal-field label { font-size: 0.72rem; color: var(--faint); text-transform: uppercase; letter-spacing: 0.07em; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
    .req { font-size: 0.65rem; color: #fbbf24; text-transform: none; letter-spacing: 0; }
    .modal-input { background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; padding: 0.75rem 1rem; color: var(--text); font-family: 'DM Sans', sans-serif; font-size: 0.9rem; outline: none; transition: border-color 0.2s; width: 100%; }
    .modal-input:focus { border-color: var(--accent); }
    .modal-submit { width: 100%; padding: 0.9rem; background: var(--accent); border: none; border-radius: 10px; color: var(--accent-text); font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s; }
    .modal-submit:hover:not(:disabled) { opacity: 0.85; }
    .modal-submit:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Confirm modal */
    .confirm-details { background: var(--surface-2); border: 1px solid var(--border); border-radius: 14px; padding: 1.25rem; margin-bottom: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
    .confirm-row { display: flex; gap: 0.75rem; font-size: 0.88rem; color: var(--text); align-items: flex-start; }
    .confirm-label { flex-shrink: 0; min-width: 80px; color: var(--muted); }
    .confirm-value { color: var(--text); font-weight: 500; }
    .confirm-divider { border: none; border-top: 1px solid var(--border); }
    .total-row { padding-top: 0.25rem; }
    .confirm-total { font-size: 1.2rem; font-weight: 800; color: var(--accent); }
    .confirm-note { font-size: 0.8rem; color: var(--faint); margin-bottom: 1.5rem; line-height: 1.5; }
    .confirm-actions { display: flex; gap: 0.75rem; }
    .confirm-cancel { flex: 1; padding: 0.85rem; border: 1px solid var(--border); border-radius: 10px; background: none; color: var(--muted); font-family: 'DM Sans', sans-serif; font-size: 0.9rem; cursor: pointer; transition: all 0.2s; }
    .confirm-cancel:hover { background: var(--surface-2); color: var(--text); }
    .confirm-go { flex: 2; padding: 0.85rem; background: var(--accent); border: none; border-radius: 10px; color: var(--accent-text); font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s; }
    .confirm-go:hover:not(:disabled) { opacity: 0.85; }
    .confirm-go:disabled { opacity: 0.5; cursor: not-allowed; }

    /* ── Currency / number-overflow safety (Rial & Toman run to millions) ──
       Totals like "۱۲٬۵۰۰٬۰۰۰ تومان" must never overflow the summary rows, the
       place-order button or the confirm dialog. Keep tabular figures and let the
       long amount wrap instead of pushing layout sideways. */
    .summary-line, .place-order-btn, .confirm-total, .confirm-value, .cart-item-price { font-variant-numeric: tabular-nums; }
    .summary-line { gap: 0.75rem; align-items: baseline; }
    .summary-line > span:last-child { text-align: right; overflow-wrap: anywhere; min-width: 0; }
    .place-order-btn { overflow-wrap: anywhere; line-height: 1.3; }
    .confirm-total { overflow-wrap: anywhere; }
    .confirm-row { flex-wrap: wrap; }
  `;
}
